import { Heading, PageHeader } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { groupBy, orderBy, uniqBy, uniqWith } from 'lodash';
import { useEffect, useState } from 'react';
import type { IServiceEmployment } from '@frontend/components/interfaces/serviceEmployment';
import { ManagerAuthorization } from '@common/enums';
import { hasOneAuthorization } from '@frontend/utils';
import type { IServiceGroupSettingsGetOne } from '../../../../../common/interfaces/serviceGroupSettingsGetOne';
import type { ICompensationPayout } from '../../../interfaces/compensationPayout';
import type { IEmployment } from '../../../interfaces/employment';
import type { IServiceConsumer } from '../../../interfaces/serviceConsumer';
import type { IServiceOrganizationSettings } from '../../../interfaces/serviceOrganizationSettings';
import type { IServiceProvider } from '../../../interfaces/serviceProvider';
import type { IServiceReaderWithServices } from '../../../interfaces/serviceReader';
import type { IServiceSupervisor } from '../../../interfaces/serviceSupervisor';
import { EpisodeCategoryRightType, ServiceRightType } from '../../../../../common/interfaces/rights';
import { isActiveOnDateOrInTheFuture } from '../../../../../common/utils/isActiveOnDate';
import { translateKey } from '../../../../translate';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { AddButtonHeader } from '../../../elements/AddButtonHeader';
import { usePersonalSettings } from '../PersonalSettingsContext';
import { PersonalSettingsCarePathDetails } from './PersonalSettingsCarePathDetails';
import { PersonalSettingsCarePathOverviewDetails } from './PersonalSettingsCarePathOverviewDetails';

export interface IEmploymentCarePaths {
    [episodeCategory: string]: {
        active: { [serviceGroupName: string]: IRight[] };
        inactive: { [serviceGroupName: string]: IRight[] };
    };
}

interface IEpisodeCategoriesAndServicesByActiveInactive {
    active: IEmploymentCarePaths;
    inactive: IEmploymentCarePaths;
}

interface IServiceRightsPerEpisodeCategoryOrService {
    [key: string]: IRight[];
}

export interface IServiceReaderRight extends IServiceReaderWithServices {
    rightType: EpisodeCategoryRightType.serviceReader;
}

export interface IServiceConsumerRight extends IServiceConsumer {
    rightType: ServiceRightType.serviceConsumer;
}

export interface IServiceProviderRight extends IServiceProvider {
    rightType: ServiceRightType.serviceProvider;
}

export interface IServiceSupervisorRight extends IServiceSupervisor {
    rightType: ServiceRightType.serviceSupervisor;
}

export interface IServiceEmploymentRight extends IServiceEmployment {
    rightType: ServiceRightType.serviceEmployment;
}

export interface IServiceGroupSettingWithServiceGroup extends IServiceGroupSettingsGetOne {
    serviceGroupName?: string;
    serviceOrganizationGroupHistoryEndDate?: Date;
    serviceOrganizationGroupHistoryStartDate?: Date;
}

export interface IServiceOrganizationSettingCompensationPayout extends ICompensationPayout {
    serviceName: string | undefined;
}

export interface ICompensationPayoutAgreements {
    groupSettingAgreements: ICompensationPayout[];
    organizationSettingAgreements: IServiceOrganizationSettingCompensationPayout[];
    practitionerAgreements: {
        serviceConsumerAgreements: ICompensationPayout[];
        serviceProviderAgreements: ICompensationPayout[];
        serviceSupervisorAgreements: ICompensationPayout[];
    };
}

export type IRight =
    | IServiceConsumerRight
    | IServiceProviderRight
    | IServiceSupervisorRight
    | IServiceReaderRight
    | IServiceEmploymentRight;

export function PersonalSettingsCarePathsOverview() {
    const { personalSettingsData } = usePersonalSettings();
    const { employmentId } = useParams() as { employmentId: string };

    const abortableFetch = useAbortableFetch();
    const [activeEmploymentCarePaths, setActiveEmploymentCarePaths] = useState<IEmploymentCarePaths>({});
    const [inactiveEmploymentCarePaths, setInactiveEmploymentCarePaths] = useState<IEmploymentCarePaths>({});
    const [groupSettings, setGroupSettings] = useState<IServiceGroupSettingWithServiceGroup[]>([]);
    const [compensationPayoutAgreements, setCompensationPayoutAgreements] = useState<ICompensationPayoutAgreements>();
    const [serviceOrganizationSettings, setServiceOrganizationSettings] = useState<IServiceOrganizationSettings[]>([]);

    useEffect(() => {
        loadPersonalSettingsCarePaths();
    }, []);

    if (!employmentId || !personalSettingsData) {
        return null;
    }

    return (
        <>
            <PageHeader title={translateKey('episodeCategory.episodeCategories')} />

            {hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit) && (
                <AddButtonHeader
                    buttonText={translateKey('episodeCategory.addEpisodeCategory')}
                    hideButton={!personalSettingsData.isActiveEmployment}
                    link={`/employments/${employmentId}/personalSettings/addCarePath`}
                    title={<Heading size="large">{translateKey('organization.activeCarepaths')}</Heading>}
                />
            )}

            <PersonalSettingsCarePathDetails
                activeEmploymentCarePaths={activeEmploymentCarePaths}
                inActiveEmploymentCarePaths={inactiveEmploymentCarePaths}
                pathname={`/employments/${employmentId}/personalSettings`}
            />
            <PersonalSettingsCarePathOverviewDetails
                activeCarePaths={activeEmploymentCarePaths}
                compensationPayoutAgreements={compensationPayoutAgreements}
                groupSettings={groupSettings}
                inactiveCarePaths={inactiveEmploymentCarePaths}
            />
        </>
    );

    async function loadPersonalSettingsCarePaths() {
        const result = await abortableFetch.fetchJson(`/api/employments/${employmentId}`, 'GET');
        const { organizationId } = result.json as IEmployment;

        const data = await getData(organizationId);
        const serviceRights = setServiceRightType(data);
        const serviceRightsPerActiveInactiveEpisodeCategory = formatRights(serviceRights);

        setServiceOrganizationSettings(data.serviceOrganizationSetting);
        setGroupSettings(data.groupSetting);
        setActiveEmploymentCarePaths(serviceRightsPerActiveInactiveEpisodeCategory.active);
        setInactiveEmploymentCarePaths(serviceRightsPerActiveInactiveEpisodeCategory.inactive);

        const payoutAgreements: ICompensationPayoutAgreements = await getCompensationPayoutAgreements(
            data.serviceConsumers,
            data.serviceProviders,
            data.serviceSupervisors,
            data.groupSetting
        );
        setCompensationPayoutAgreements(payoutAgreements);
    }

    async function getData(organizationId: string) {
        const [
            serviceEmploymentResults,
            serviceConsumersResults,
            serviceProvidersResults,
            serviceSupervisorsResults,
            serviceReaderResults,
            serviceOrganizationSettingResults,
            serviceGroupSettingResults,
        ] = await Promise.all([
            abortableFetch.fetchJson(
                `/api/serviceEmployments?employmentId=${employmentId}&includeServiceGroups=true&includeActivityAuthorizations=true`,
                'GET'
            ),
            abortableFetch.fetchJson(
                `/api/serviceConsumers?employmentId=${employmentId}&includeServiceGroupAndEpisodeCategory=true`,
                'GET'
            ),
            abortableFetch.fetchJson(
                `/api/serviceProviders?employmentId=${employmentId}&includeServiceGroups=true`,
                'GET'
            ),
            abortableFetch.fetchJson(
                `/api/serviceSupervisors?employmentId=${employmentId}&includeServiceGroups=true`,
                'GET'
            ),
            abortableFetch.fetchJson(
                `/api/serviceReaders?employmentId=${employmentId}&includeItemAuthorizations=canUpdate&includeListAuthorizations=canAdd&includeServicesAndServiceGroups=true`,
                'GET'
            ),
            abortableFetch.fetchJson(`/api/serviceOrganizationSettings?organizationId=${organizationId}`, 'GET'),
            abortableFetch.fetchJson(
                `/api/serviceGroupSettings?organizationId=${organizationId}&includeServiceGroups=true`,
                'GET'
            ),
        ]);

        const serviceEmployments: IServiceEmployment[] = serviceEmploymentResults.json.items;
        const serviceConsumers: IServiceConsumer[] = serviceConsumersResults.json.items;
        const serviceProviders: IServiceProvider[] = serviceProvidersResults.json.items;
        const serviceSupervisors: IServiceSupervisor[] = serviceSupervisorsResults.json.items;
        const serviceReaders: IServiceReaderWithServices[] = serviceReaderResults.json.items;
        const serviceOrganizationSetting: IServiceOrganizationSettings[] = serviceOrganizationSettingResults.json.items;
        const groupSetting: IServiceGroupSettingWithServiceGroup[] =
            filterServiceGroupSettingsOnServiceOrganizationSettings(
                serviceGroupSettingResults.json.items,
                serviceOrganizationSetting
            );

        return {
            groupSetting,
            serviceConsumers,
            serviceEmployments,
            serviceOrganizationSetting,
            serviceProviders,
            serviceReaders,
            serviceSupervisors,
        };
    }

    function filterServiceGroupSettingsOnServiceOrganizationSettings(
        groupSetting: IServiceGroupSettingWithServiceGroup[],
        organizationSetting: IServiceOrganizationSettings[]
    ) {
        const isValidGroupSetting = (
            oneGroupSetting: IServiceGroupSettingWithServiceGroup,
            oneOrganizationSetting: IServiceOrganizationSettings
        ) => {
            if (
                oneGroupSetting.service.id === oneOrganizationSetting.serviceId &&
                oneOrganizationSetting.serviceOrganizationGroupHistory.length &&
                oneGroupSetting.id ===
                    oneOrganizationSetting.serviceOrganizationGroupHistory[0].serviceGroupSettingId &&
                oneGroupSetting.groupId ===
                    oneOrganizationSetting.serviceOrganizationGroupHistory[0].serviceGroupSetting?.group.id
            ) {
                return true;
            }

            return false;
        };

        const filteredOrganizationSettings = organizationSetting.filter(
            (orgSetting) =>
                isActiveOnDateOrInTheFuture(orgSetting, new Date()) &&
                orgSetting.serviceOrganizationGroupHistory.length &&
                isActiveOnDateOrInTheFuture(orgSetting.serviceOrganizationGroupHistory[0], new Date())
        );
        const filteredGroupSettings = uniqBy(
            groupSetting.filter((setting) =>
                filteredOrganizationSettings.some((orgSetting) => isValidGroupSetting(setting, orgSetting))
            ),
            'id'
        );

        return filteredGroupSettings;
    }

    function setServiceRightType(data: {
        groupSetting: IServiceGroupSettingsGetOne[];
        serviceConsumers: IServiceConsumer[];
        serviceEmployments: IServiceEmployment[];
        serviceProviders: IServiceProvider[];
        serviceReaders: IServiceReaderWithServices[];
        serviceSupervisors: IServiceSupervisor[];
    }) {
        const serviceEmploymentFormatted: IServiceEmploymentRight[] = data.serviceEmployments.map(
            (result: IServiceEmployment) => {
                return { ...result, rightType: ServiceRightType.serviceEmployment };
            }
        );

        const serviceConsumerFormatted: IServiceConsumerRight[] = data.serviceConsumers.map(
            (result: IServiceConsumer) => {
                return { ...result, rightType: ServiceRightType.serviceConsumer };
            }
        );
        const serviceProviderFormatted: IServiceProviderRight[] = data.serviceProviders.map(
            (result: IServiceProvider) => {
                return { ...result, rightType: ServiceRightType.serviceProvider };
            }
        );
        const serviceSupervisorFormatted: IServiceSupervisorRight[] = data.serviceSupervisors.map(
            (result: IServiceSupervisor) => {
                return { ...result, rightType: ServiceRightType.serviceSupervisor };
            }
        );
        const serviceReadersFormatted: IServiceReaderRight[] = data.serviceReaders.map(
            (result: IServiceReaderWithServices) => {
                return { ...result, rightType: EpisodeCategoryRightType.serviceReader };
            }
        );

        return [
            ...serviceEmploymentFormatted,
            ...serviceConsumerFormatted,
            ...serviceProviderFormatted,
            ...serviceSupervisorFormatted,
            ...serviceReadersFormatted,
        ];
    }

    function formatRights(serviceRights: IRight[]) {
        const serviceRightsPerEpisodeCategory = groupBy(
            serviceRights,
            (serviceRight) => serviceRight.service.episodeCategory.name
        );
        const serviceRightsPerActiveInactiveEpisodeCategory = sortServiceRightsPerActiveInactiveEpisodeCategory(
            serviceRightsPerEpisodeCategory
        );

        return serviceRightsPerActiveInactiveEpisodeCategory;
    }

    function sortServiceRightsPerActiveInactiveEpisodeCategory(
        serviceRightsPerEpisodeCategory: IServiceRightsPerEpisodeCategoryOrService
    ) {
        const episodeCategoriesAndServicesByActiveInactive: IEpisodeCategoriesAndServicesByActiveInactive = {
            active: {},
            inactive: {},
        };

        Object.entries(serviceRightsPerEpisodeCategory).forEach((entry) => {
            const episodeCategory = entry[0];
            const rights = entry[1];
            const activeOnDateOrInTheFutureRights = rights.filter((right) =>
                isActiveOnDateOrInTheFuture(right, new Date())
            );

            if (activeOnDateOrInTheFutureRights.length > 0) {
                episodeCategoriesAndServicesByActiveInactive.active[episodeCategory] = { active: {}, inactive: {} };
                const groupedByServiceGroup = groupBy(rights, (right) => right.serviceGroupName);
                Object.keys(groupedByServiceGroup).forEach((serviceGroupName) => {
                    const activeServiceGroupRights = groupedByServiceGroup[serviceGroupName].filter((right) =>
                        isActiveOnDateOrInTheFuture(right, new Date())
                    );
                    if (activeServiceGroupRights.length > 0) {
                        episodeCategoriesAndServicesByActiveInactive.active[episodeCategory].active[serviceGroupName] =
                            activeServiceGroupRights;
                    } else {
                        const inactiveServiceGroupRights = removeNonUniqueInactiveServices(
                            groupedByServiceGroup[serviceGroupName]
                        );
                        episodeCategoriesAndServicesByActiveInactive.active[episodeCategory].inactive[
                            serviceGroupName
                        ] = inactiveServiceGroupRights;
                    }
                });
            } else {
                episodeCategoriesAndServicesByActiveInactive.inactive[episodeCategory] = { active: {}, inactive: {} };
                const inactiveRights = removeNonUniqueInactiveServices(rights);
                const inactiveRightsGroupedByServiceGroup = groupBy(inactiveRights, (right) => right.serviceGroupName);
                Object.keys(inactiveRightsGroupedByServiceGroup).forEach((serviceGroupName) => {
                    episodeCategoriesAndServicesByActiveInactive.inactive[episodeCategory].inactive[serviceGroupName] =
                        inactiveRightsGroupedByServiceGroup[serviceGroupName];
                });
            }
        });

        return episodeCategoriesAndServicesByActiveInactive;
    }

    function removeNonUniqueInactiveServices(rights: IRight[]) {
        const servicesOrderedByEndDate = orderBy(rights, 'endDate', 'desc');

        return uniqWith(
            servicesOrderedByEndDate,
            (a, b) => a.service.name === b.service.name && a.rightType === b.rightType
        );
    }

    async function getCompensationPayoutAgreements(
        serviceConsumers: IServiceConsumer[],
        serviceProviders: IServiceProvider[],
        serviceSupervisors: IServiceSupervisor[],
        groupSetting: IServiceGroupSettingWithServiceGroup[]
    ) {
        const url = buildCompensationPayoutUrl(serviceConsumers, serviceProviders, serviceSupervisors, groupSetting);
        const agreements: ICompensationPayout[] = (await abortableFetch.fetchJson(url, 'GET')).json.items;

        const organizationSettingAgreements: ICompensationPayout[] = agreements.filter(
            (agreement) => !!agreement.serviceOrganizationSettingId
        );
        const organizationSettingAgreementsWithService: IServiceOrganizationSettingCompensationPayout[] =
            organizationSettingAgreements.map((agreement) => {
                const serviceOrganizationSetting = serviceOrganizationSettings.find(
                    (setting) => agreement.serviceOrganizationSettingId === setting.id
                );

                return { ...agreement, serviceName: serviceOrganizationSetting?.service.name };
            });

        const groupSettingAgreements: ICompensationPayout[] = agreements.filter(
            (agreement) => !!agreement.serviceGroupSettingId
        );
        const serviceSupervisorAgreements: ICompensationPayout[] = agreements.filter(
            (agreement) => !!agreement.serviceSupervisorId
        );
        const serviceConsumerAgreements: ICompensationPayout[] = agreements.filter(
            (agreement) => !!agreement.serviceConsumerId
        );
        const serviceProviderAgreements: ICompensationPayout[] = agreements.filter(
            (agreement) => !!agreement.serviceProviderId
        );

        return {
            groupSettingAgreements,
            organizationSettingAgreements: organizationSettingAgreementsWithService,
            practitionerAgreements: {
                serviceConsumerAgreements,
                serviceProviderAgreements,
                serviceSupervisorAgreements,
            },
        };
    }

    function buildCompensationPayoutUrl(
        serviceConsumers: IServiceConsumer[],
        serviceProviders: IServiceProvider[],
        serviceSupervisors: IServiceSupervisor[],
        groupSetting: IServiceGroupSettingsGetOne[]
    ) {
        let url = '/api/compensationPayouts?';

        url += buildUrl(serviceOrganizationSettings, 'serviceOrganizationSetting');
        url += buildUrl(groupSetting, 'serviceGroupSetting');
        url += buildUrl(serviceConsumers, 'serviceConsumer');
        url += buildUrl(serviceProviders, 'serviceProvider');
        url += buildUrl(serviceSupervisors, 'serviceSupervisor');

        return url;
    }

    function buildUrl(array: { id: string }[], name: string) {
        let url = '';
        if (array.length === 1) {
            url += `&${name}Id=${array[0].id}`;
        } else if (array.length > 1) {
            url += `&${array.map((item) => `${name}IdList=${item.id}`).join('&')}`;
        }

        return url;
    }
}
